import React from 'react';
import PropTypes from 'prop-types';
import { trainerLook } from './trainerLook';

/** The back of the hair, drawn before the face. */
function hairBack({ girl, hairStyle, hair }) {
  if (!girl) {
    return null;
  }
  if (hairStyle === 'long') {
    return <path d="M17 30 Q16 52 22 58 L42 58 Q48 52 47 30 Z" fill={hair} />;
  }
  if (hairStyle === 'ponytail') {
    return <path d="M44 24 Q56 30 52 46 Q50 40 46 36 Z" fill={hair} />;
  }
  return <path d="M18 28 Q17 44 22 46 L42 46 Q47 44 46 28 Z" fill={hair} />;
}

/** The hair on top, drawn over the face; a hat covers most of it. */
function hairFront({ hairStyle, hair, hat }) {
  if (hat) {
    return <path d="M20 30 Q20 36 23 37 L23 31 Z M44 30 Q44 36 41 37 L41 31 Z" fill={hair} />;
  }
  switch (hairStyle) {
    case 'spiky':
      return <path d="M19 30 L18 20 L24 23 L26 14 L31 21 L35 13 L38 21 L44 16 L44 24 L46 30 Q40 22 32 23 Q24 22 19 30 Z" fill={hair} />;
    case 'side':
      return <path d="M19 31 Q18 17 32 16 Q46 17 45 31 Q42 22 30 24 Q24 25 19 31 Z" fill={hair} />;
    case 'long':
    case 'bob':
    case 'ponytail':
      return <path d="M18 32 Q17 15 32 15 Q47 15 46 32 Q44 22 32 22 Q22 22 18 32 Z" fill={hair} />;
    default:
      return <path d="M19 30 Q19 16 32 16 Q45 16 45 30 Q41 21 32 21 Q23 21 19 30 Z" fill={hair} />;
  }
}

function hat({ hat: kind, hatColor }) {
  if (kind === 'cap') {
    return (
      <g>
        <path d="M18 27 Q18 13 32 13 Q46 13 46 27 Z" fill={hatColor} />
        <path d="M30 27 Q42 24 52 28 Q42 31 30 29 Z" fill={hatColor} />
        <rect x="18" y="25" width="28" height="3" rx="1.5" fill="rgba(0,0,0,.18)" />
        <circle cx="32" cy="19" r="3" fill="#fff" />
      </g>
    );
  }
  if (kind === 'beanie') {
    return (
      <g>
        <path d="M18 28 Q17 12 32 12 Q47 12 46 28 Z" fill={hatColor} />
        <rect x="17" y="24" width="30" height="6" rx="3" fill={hatColor} />
        <rect x="17" y="24" width="30" height="6" rx="3" fill="rgba(255,255,255,.22)" />
        <circle cx="32" cy="11" r="3.5" fill={hatColor} />
      </g>
    );
  }
  return null;
}

/**
 * A computer opponent's picture: a cartoon trainer of our own, varied by the seed (trainerLook), so each game's
 * computer looks like someone.
 */
const TrainerAvatar = ({ seed }) => {
  const look = trainerLook(seed);
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%" aria-hidden="true">
      <rect width="64" height="64" fill={look.background} />
      {hairBack(look)}
      {/* Shoulders and shirt. */}
      <path d="M12 64 Q14 48 32 48 Q50 48 52 64 Z" fill={look.shirt} />
      <path d="M27 48 L32 54 L37 48 Z" fill={look.skin} />
      {/* Face, ears, neck. */}
      <rect x="28" y="40" width="8" height="9" fill={look.skin} />
      <circle cx="20" cy="33" r="3" fill={look.skin} />
      <circle cx="44" cy="33" r="3" fill={look.skin} />
      <ellipse cx="32" cy="32" rx="12" ry="13" fill={look.skin} />
      {hairFront(look)}
      {hat(look)}
      {/* Eyes, cheeks, smile. */}
      <ellipse cx="27" cy="33" rx="1.6" ry="2.2" fill="#1f1a17" />
      <ellipse cx="37" cy="33" rx="1.6" ry="2.2" fill="#1f1a17" />
      <circle cx="24" cy="37" r="1.8" fill="rgba(230,90,90,.28)" />
      <circle cx="40" cy="37" r="1.8" fill="rgba(230,90,90,.28)" />
      <path d="M28.5 38.5 Q32 41.5 35.5 38.5" stroke="#1f1a17" strokeWidth="1.3" fill="none" strokeLinecap="round" />
    </svg>
  );
};

TrainerAvatar.propTypes = {
  seed: PropTypes.number.isRequired,
};

export default TrainerAvatar;
